import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Clock, 
  Star, 
  Play, 
  Filter,
  Search,
  Award,
  Target,
  TrendingUp
} from 'lucide-react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const QuizContainer = styled.div`
  min-height: 100vh;
  background: var(--color-background);
`;

const Header = styled.section`
  background: var(--gradient-primary);
  color: white;
  padding: var(--spacing-2xl) 0;
  text-align: center;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-md);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Controls = styled.div`
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  padding: var(--spacing-lg) 0;
  position: sticky;
  top: 70px;
  z-index: var(--z-sticky);
`;

const ControlsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  display: flex;
  gap: var(--spacing-lg);
  align-items: center;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  
  .search-icon {
    position: absolute;
    left: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: var(--spacing-md) var(--spacing-md) var(--spacing-md) 48px;
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  background: var(--color-background);
  color: var(--color-text-primary);
  transition: border-color var(--animation-duration) ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-background);
  color: var(--color-text-secondary);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  
  &.active {
    border-color: var(--color-primary);
    background: var(--color-primary);
    color: white;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const Content = styled.section`
  padding: var(--spacing-2xl) 0;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const QuizGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-2xl);
`;

const QuizCard = styled(motion.div)`
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  transition: all var(--animation-duration) ease;
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    border-color: var(--color-primary);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    transform: scaleX(0);
    transition: transform var(--animation-duration) ease;
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
`;

const QuizHeader = styled.div`
  padding: var(--spacing-xl);
  border-bottom: 1px solid var(--color-border);
`;

const QuizTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
  line-height: 1.3;
`;

const QuizDescription = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin-bottom: var(--spacing-md);
`;

const QuizMeta = styled.div`
  display: flex;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
  
  .meta-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    font-size: 0.875rem;
    color: var(--color-text-muted);
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const DifficultyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &.beginner {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }
  
  &.intermediate {
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  }
  
  &.advanced {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
`;

const QuizStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .stats {
    display: flex;
    gap: var(--spacing-md);
    
    .stat {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      font-size: 0.875rem;
      color: var(--color-text-muted);
      
      svg {
        width: 16px;
        height: 16px;
      }
      
      .value {
        font-weight: 600;
        color: var(--color-text-primary);
      }
    }
  }
`;

const QuizActions = styled.div`
  padding: var(--spacing-lg) var(--spacing-xl);
  background: var(--color-surface);
`;

const StartButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-md);
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    text-decoration: none;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: var(--spacing-2xl);
  color: var(--color-text-muted);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-2xl);
  color: var(--color-text-muted);
  
  .icon {
    width: 64px;
    height: 64px;
    margin: 0 auto var(--spacing-lg);
    color: var(--color-text-muted);
    opacity: 0.5;
  }
  
  h3 {
    font-size: 1.25rem;
    margin-bottom: var(--spacing-sm);
    color: var(--color-text-secondary);
  }
`;

const StatsSection = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
`;

const StatsTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--color-primary);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
`;

const StatCard = styled.div`
  text-align: center;
  padding: var(--spacing-lg);
  background: var(--color-background);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-border);
  
  .stat-number {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-primary);
    margin-bottom: var(--spacing-xs);
  }
  
  .stat-label {
    color: var(--color-text-secondary);
    font-size: 0.875rem;
  }
`;

const Quiz = () => {
  const { isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  
  // 获取测验列表
  const { data: quizData, isLoading, error } = useQuery(
    ['quizzes', { difficulty: difficultyFilter !== 'all' ? difficultyFilter : undefined }],
    async () => {
      const params = new URLSearchParams();
      if (difficultyFilter !== 'all') {
        params.append('difficulty', difficultyFilter);
      }
      const response = await axios.get(`/api/quiz?${params.toString()}`);
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // 获取用户统计（如果已登录）
  const { data: userStats } = useQuery(
    'user-stats',
    async () => {
      const response = await axios.get('/api/users/stats');
      return response.data.data;
    },
    {
      enabled: isAuthenticated,
      staleTime: 5 * 60 * 1000,
    }
  );

  // 过滤测验
  const filteredQuizzes = React.useMemo(() => {
    if (!quizData?.data) return [];
    
    return quizData.data.filter(quiz => {
      const matchesSearch = quiz.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           quiz.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           quiz.dynasty?.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  }, [quizData, searchTerm]);

  const getDifficultyText = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return '初级';
      case 'intermediate': return '中级';
      case 'advanced': return '高级';
      default: return difficulty;
    }
  };

  if (isLoading) {
    return (
      <QuizContainer>
        <Header>
          <HeaderContent>
            <Title>知识测验</Title>
            <Subtitle>通过测验检验您的历史知识，在学习中不断进步</Subtitle>
          </HeaderContent>
        </Header>
        <LoadingContainer>
          <LoadingSpinner />
          <p>正在加载测验...</p>
        </LoadingContainer>
      </QuizContainer>
    );
  }

  if (error) {
    return (
      <QuizContainer>
        <Header>
          <HeaderContent>
            <Title>知识测验</Title>
            <Subtitle>通过测验检验您的历史知识，在学习中不断进步</Subtitle>
          </HeaderContent>
        </Header>
        <ErrorMessage>
          <p>加载测验时出现错误，请稍后重试。</p>
        </ErrorMessage>
      </QuizContainer>
    );
  }

  return (
    <QuizContainer>
      <Header>
        <HeaderContent>
          <Title>知识测验</Title>
          <Subtitle>通过测验检验您的历史知识，在学习中不断进步</Subtitle>
        </HeaderContent>
      </Header>

      <Controls>
        <ControlsContent>
          <SearchBox>
            <Search className="search-icon" />
            <SearchInput
              type="text"
              placeholder="搜索测验标题、描述或朝代..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
          
          <FilterButton
            className={difficultyFilter === 'all' ? 'active' : ''}
            onClick={() => setDifficultyFilter('all')}
          >
            <Filter />
            全部难度
          </FilterButton>
          
          <FilterButton
            className={difficultyFilter === 'beginner' ? 'active' : ''}
            onClick={() => setDifficultyFilter('beginner')}
          >
            <Target />
            初级
          </FilterButton>
          
          <FilterButton
            className={difficultyFilter === 'intermediate' ? 'active' : ''}
            onClick={() => setDifficultyFilter('intermediate')}
          >
            <TrendingUp />
            中级
          </FilterButton>
          
          <FilterButton
            className={difficultyFilter === 'advanced' ? 'active' : ''}
            onClick={() => setDifficultyFilter('advanced')}
          >
            <Award />
            高级
          </FilterButton>
        </ControlsContent>
      </Controls>

      <Content>
        <ContentWrapper>
          {isAuthenticated && userStats && (
            <StatsSection>
              <StatsTitle>
                <TrendingUp />
                您的学习统计
              </StatsTitle>
              <StatsGrid>
                <StatCard>
                  <div className="stat-number">{userStats.stats.totalQuizzes}</div>
                  <div className="stat-label">已完成测验</div>
                </StatCard>
                <StatCard>
                  <div className="stat-number">{userStats.stats.accuracy}%</div>
                  <div className="stat-label">正确率</div>
                </StatCard>
                <StatCard>
                  <div className="stat-number">{userStats.stats.studyStreak}</div>
                  <div className="stat-label">连续学习天数</div>
                </StatCard>
                <StatCard>
                  <div className="stat-number">{userStats.progress.dynastyProgress.length}</div>
                  <div className="stat-label">已学习朝代</div>
                </StatCard>
              </StatsGrid>
            </StatsSection>
          )}
          
          {filteredQuizzes.length === 0 ? (
            <EmptyState>
              <BookOpen className="icon" />
              <h3>没有找到测验</h3>
              <p>请尝试调整搜索条件或筛选器</p>
            </EmptyState>
          ) : (
            <QuizGrid>
              {filteredQuizzes.map((quiz, index) => (
                <QuizCard
                  key={quiz._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <QuizHeader>
                    <QuizTitle>{quiz.title}</QuizTitle>
                    <QuizDescription>{quiz.description}</QuizDescription>
                    
                    <QuizMeta>
                      <div className="meta-item">
                        <BookOpen />
                        <span>{quiz.dynasty?.name || '综合'}</span>
                      </div>
                      <div className="meta-item">
                        <Clock />
                        <span>{quiz.timeLimit}分钟</span>
                      </div>
                    </QuizMeta>
                    
                    <QuizStats>
                      <DifficultyBadge className={quiz.difficulty}>
                        {getDifficultyText(quiz.difficulty)}
                      </DifficultyBadge>
                      
                      <div className="stats">
                        <div className="stat">
                          <Star />
                          <span className="value">{quiz.stats?.averageScore || 0}</span>
                          <span>平均分</span>
                        </div>
                        <div className="stat">
                          <Award />
                          <span className="value">{quiz.stats?.passRate || 0}%</span>
                          <span>通过率</span>
                        </div>
                      </div>
                    </QuizStats>
                  </QuizHeader>
                  
                  <QuizActions>
                    <StartButton to={`/quiz/${quiz._id}`}>
                      <Play />
                      开始测验
                    </StartButton>
                  </QuizActions>
                </QuizCard>
              ))}
            </QuizGrid>
          )}
        </ContentWrapper>
      </Content>
    </QuizContainer>
  );
};

export default Quiz;
